import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import supabase from '../supabaseClient';
import { TABLE_NAMES } from '../config';
import { getSubscriptionState } from '../lib/subscriptionService';
import { getAdminDemo } from '../lib/adminDemoSession';

const CommunityContext = createContext(null);

export const CommunityProvider = ({ children }) => {
  const [contextReady, setContextReady] = useState(false);
  const [userId, setUserId] = useState(null);
  const [communityId, setCommunityId] = useState(null);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [communityName, setCommunityName] = useState('');
  const [communityLogo, setCommunityLogo] = useState('');
  const [communitySport, setCommunitySport] = useState('Football');
  const [managerName, setManagerName] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [billingStatus, setBillingStatus] = useState('inactive');
  const [paymentMethodLast4, setPaymentMethodLast4] = useState(null);
  const [paymentMethodBrand, setPaymentMethodBrand] = useState(null);
  const [defaultPaymentMethodToken, setDefaultPaymentMethodToken] = useState(null);
  const [subscriptionStartedAt, setSubscriptionStartedAt] = useState(null);
  const [subscriptionCurrentPeriodStart, setSubscriptionCurrentPeriodStart] = useState(null);
  const [subscriptionCurrentPeriodEnd, setSubscriptionCurrentPeriodEnd] = useState(null);
  const [subscriptionCanceledAt, setSubscriptionCanceledAt] = useState(null);
  const [subscriptionAmountCents, setSubscriptionAmountCents] = useState(null);
  const [subscriptionCurrency, setSubscriptionCurrency] = useState('USD');
  const [isOwner, setIsOwner] = useState(false);
  const [communityIsPrivate, setCommunityIsPrivate] = useState(false);
  const [isAdminDemo, setIsAdminDemo] = useState(false);

  useEffect(() => {
    let alive = true;
    let request = 0;
    const load = async () => {
      const ticket = ++request;
      const current = () => alive && ticket === request;
      try {
        const { data: authData, error: authError } = await supabase.auth.getUser();
        if (!current()) return;
        if (authError || !authData?.user?.id) { setContextReady(true); return; }

        const uid = authData.user.id;
        setUserId(uid);

        const { data: profile, error: profileError } = await supabase
          .from(TABLE_NAMES.USERS)
          .select('community_id, name, email')
          .eq('user_id', uid)
          .single();

        const demo = getAdminDemo('community', uid);
        const effectiveCommunityId = demo?.orgId || profile?.community_id;
        if (!current()) return;

        if ((profileError && !demo) || !effectiveCommunityId) {
          setIsSetupMode(false);
          setContextReady(true);
          return;
        }

        setIsAdminDemo(!!demo);
        setCommunityId(effectiveCommunityId);
        setManagerName(profile?.name || '');
        setManagerEmail(profile?.email || '');

        let community = null;
        const { data: communityData, error: communityError } = await supabase
          .from(TABLE_NAMES.COMMUNITIES)
          .select('*')
          .eq('id', effectiveCommunityId)
          .single();

        if (!communityError) {
          community = communityData;
        }

        if (!current()) return;
        if (community) {
          setIsSetupMode(false);
          setCommunityName(community.name || '');
          setCommunityLogo(community.logo_url || '');
          const sport = String(community.sport || 'Football').trim();
          setCommunitySport(sport || 'Football');
          setIsOwner(!!demo || community.created_by === uid);
          setCommunityIsPrivate(false);
          if (community.billing_status !== undefined) {
            setBillingStatus(community.billing_status || 'inactive');
            setPaymentMethodLast4(community.payment_method_last4 || null);
            setPaymentMethodBrand(community.payment_method_brand || null);
            setDefaultPaymentMethodToken(community.default_payment_method_token || null);
          }
          setSubscriptionStartedAt(community.subscription_started_at || null);
          setSubscriptionCurrentPeriodStart(community.subscription_current_period_start || null);
          setSubscriptionCurrentPeriodEnd(community.subscription_current_period_end || null);
          setSubscriptionCanceledAt(community.subscription_canceled_at || null);
          setSubscriptionAmountCents(community.subscription_amount_cents ?? null);
          setSubscriptionCurrency(community.subscription_currency || 'USD');
        } else {
          setIsSetupMode(false);
        }

        if (community?.name) localStorage.setItem('communityName', community.name);
        if (profile?.name) localStorage.setItem('managerName', profile.name);
      } catch (err) {
        console.error('CommunityContext: failed to load', err);
      } finally {
        if (current()) setContextReady(true);
      }
    };
    load();
    const onStorage = (event) => {
      if (event.key && event.key !== 'statboxAdminDemo') return;
      load();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      alive = false;
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const refreshCommunity = useCallback(async () => {
    if (!communityId) return;
    const { data, error } = await supabase
      .from('communities')
      .select('*')
      .eq('id', communityId)
      .single();
    if (error || !data) return;
    setIsSetupMode(false);
    setCommunityName(data.name || '');
    setCommunityLogo(data.logo_url || '');
    setCommunitySport(String(data.sport || 'Football').trim() || 'Football');
    setCommunityIsPrivate(false);
    if (data.billing_status !== undefined) {
      setBillingStatus(data.billing_status || 'inactive');
      setPaymentMethodLast4(data.payment_method_last4 || null);
      setPaymentMethodBrand(data.payment_method_brand || null);
      setDefaultPaymentMethodToken(data.default_payment_method_token || null);
    }
    setSubscriptionStartedAt(data.subscription_started_at || null);
    setSubscriptionCurrentPeriodStart(data.subscription_current_period_start || null);
    setSubscriptionCurrentPeriodEnd(data.subscription_current_period_end || null);
    setSubscriptionCanceledAt(data.subscription_canceled_at || null);
    setSubscriptionAmountCents(data.subscription_amount_cents ?? null);
    setSubscriptionCurrency(data.subscription_currency || 'USD');
  }, [communityId]);

  const subscription = useMemo(() => getSubscriptionState({
    billing_status: billingStatus,
    subscription_current_period_end: subscriptionCurrentPeriodEnd,
    subscription_canceled_at: subscriptionCanceledAt,
  }), [billingStatus, subscriptionCurrentPeriodEnd, subscriptionCanceledAt]);

  return (
    <CommunityContext.Provider value={{
      contextReady,
      userId,
      communityId,
      isSetupMode,
      communityName,
      communityLogo,
      communitySport,
      managerName,
      managerEmail,
      billingStatus,
      paymentMethodLast4,
      paymentMethodBrand,
      defaultPaymentMethodToken,
      subscriptionStartedAt,
      subscriptionCurrentPeriodStart,
      subscriptionCurrentPeriodEnd,
      subscriptionCanceledAt,
      subscriptionAmountCents,
      subscriptionCurrency,
      subscription,
      isSubscriptionActive: subscription.isActive,
      isOwner,
      communityIsPrivate,
      isAdminDemo,
      refreshCommunity,
    }}>
      {children}
    </CommunityContext.Provider>
  );
};

export const useCommunity = () => {
  const ctx = useContext(CommunityContext);
  if (!ctx) throw new Error('useCommunity must be used within CommunityProvider');
  return ctx;
};

export const useCommunityOptional = () => useContext(CommunityContext);
